import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

export const useScheduledNotifications = () => {
    const [scheduledNotifications, setScheduledNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const user = useSelector(state => state.userController.user);

    const fetchScheduledNotifications = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scheduled-notifications`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setScheduledNotifications(json);
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
        } finally {
            setIsLoading(false);
        }
    };

    const createScheduledNotification = async (data) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scheduled-notifications`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setScheduledNotifications(prev => [json.scheduledNotification, ...prev]);
            dispatch(alertActions.showAlert({ msg: 'تم إنشاء الجدولة بنجاح', type: 'success' }));
            return json.scheduledNotification;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    const updateScheduledNotification = async (id, data) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scheduled-notifications/${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setScheduledNotifications(prev => prev.map(item => item._id === id ? json.scheduledNotification : item));
            dispatch(alertActions.showAlert({ msg: 'تم تحديث الجدولة بنجاح', type: 'success' }));
            return json.scheduledNotification;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    const toggleScheduledNotificationStatus = async (id, status) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scheduled-notifications/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setScheduledNotifications(prev => prev.map(item => item._id === id ? json.scheduledNotification : item));
            dispatch(alertActions.showAlert({
                msg: status === 'paused' ? 'تم إيقاف الجدولة مؤقتًا' : 'تم استئناف الجدولة',
                type: 'success'
            }));
            return json.scheduledNotification;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    const deleteScheduledNotification = async (id) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scheduled-notifications/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setScheduledNotifications(prev => prev.filter(item => item._id !== id));
            dispatch(alertActions.showAlert({ msg: 'تم حذف الجدولة بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    const fetchScheduledNotificationHistory = async (id) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scheduled-notifications/${id}/history`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return [];
        }
    };

    const clearScheduledNotificationHistory = async (id) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scheduled-notifications/${id}/history`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            dispatch(alertActions.showAlert({ msg: 'تم مسح السجل بنجاح', type: 'success' }));
            return true;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return false;
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchScheduledNotifications();
        }
    }, [user]);

    return {
        scheduledNotifications,
        isLoading,
        fetchScheduledNotifications,
        createScheduledNotification,
        updateScheduledNotification,
        toggleScheduledNotificationStatus,
        deleteScheduledNotification,
        fetchScheduledNotificationHistory,
        clearScheduledNotificationHistory
    };
};
